import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Button,
  Card,
  Chip,
  Grid,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import {
  collection,
  doc,
  getDoc,
  getDocs,
  orderBy,
  query,
} from "firebase/firestore";
import MainLayout from "../../layouts/main_layout/main_layout";
import { useFirestore } from "reactfire";
import {
  REDON_PAGE_SETTINGS_COLLECTION,
  REDON_PAGE_SETTINGS_DOC_ID,
} from "../admin_portal/redon/redon_page_settings_tab";

type RedonPageSettings = {
  description: string;
  diocesanInchargeName: string;
  diocesanInchargeContact: string;
};

type RedonMonthlySlide = {
  docId: string;
  monthKey: string;
  monthLabel: string;
  images: string[];
};

const REDON_LOGO_CANDIDATES = [
  "/images/redon/logo.png",
  "/images/redon/redon-logo.png",
  "/images/redon/redon_logo.png",
];

const DEFAULT_DESCRIPTION = `REDON is the official blood donors network of ICYM Udupi Diocese, dedicated to responding to emergency blood requirements for anyone in need. Through this initiative, the youth of the diocese come forward with the noble act of blood donation, offering timely support to those in critical situations.

Though REDON has been actively functioning for several years, it was officially named and inaugurated on 25th May 2025, marking a significant milestone in its mission of service.

The communication and coordination of REDON are managed by the ICYM Diocesan Joint Secretary / REDON In-charge, in collaboration with Deanery REDON In-charges and Unit In-charges. The initiative operates under the guidance and support of the Director and the ICYM Central Council.`;

function getCurrentMonthKey() {
  return new Date().toISOString().slice(0, 7);
}

export default function RedonPage() {
  const firestore = useFirestore();
  const [pageSettings, setPageSettings] = useState<RedonPageSettings>({
    description: DEFAULT_DESCRIPTION,
    diocesanInchargeName: "",
    diocesanInchargeContact: "",
  });
  const [monthlySlides, setMonthlySlides] = useState<RedonMonthlySlide[]>([]);
  const [selectedMonthKey, setSelectedMonthKey] = useState("");
  const [currentSlideIndex, setCurrentSlideIndex] = useState(0);
  const [logoIndex, setLogoIndex] = useState(0);

  useEffect(() => {
    let isMounted = true;

    async function loadRedonData() {
      try {
        const [settingsSnapshot, slidesSnapshot] = await Promise.all([
          getDoc(
            doc(
              firestore,
              REDON_PAGE_SETTINGS_COLLECTION,
              REDON_PAGE_SETTINGS_DOC_ID,
            ),
          ),
          getDocs(
            query(
              collection(firestore, "redonMonthlySlides"),
              orderBy("monthKey", "desc"),
            ),
          ),
        ]);

        if (!isMounted) {
          return;
        }

        if (settingsSnapshot.exists()) {
          setPageSettings((prev) => ({
            ...prev,
            ...(settingsSnapshot.data() as Partial<RedonPageSettings>),
          }));
        }

        const slides = slidesSnapshot.docs.map((slideDoc) => ({
          docId: slideDoc.id,
          ...(slideDoc.data() as Omit<RedonMonthlySlide, "docId">),
        }));
        setMonthlySlides(slides);

        const currentMonthKey = getCurrentMonthKey();
        const currentMonthSlide = slides.find(
          (slide) => slide.monthKey === currentMonthKey,
        );
        setSelectedMonthKey(
          currentMonthSlide?.monthKey || slides[0]?.monthKey || "",
        );
      } catch (error) {
        console.error("Failed to load REDON page data", error);
      }
    }

    loadRedonData();

    return () => {
      isMounted = false;
    };
  }, [firestore]);

  const selectedMonth = useMemo(
    () =>
      monthlySlides.find((slide) => slide.monthKey === selectedMonthKey) || null,
    [monthlySlides, selectedMonthKey],
  );

  const activeSlides = selectedMonth?.images || [];
  const activeSlideImage = activeSlides[currentSlideIndex] || "";

  useEffect(() => {
    setCurrentSlideIndex(0);
  }, [selectedMonthKey]);

  useEffect(() => {
    if (activeSlides.length <= 1) {
      return;
    }

    const interval = window.setInterval(() => {
      setCurrentSlideIndex((prev) => (prev + 1) % activeSlides.length);
    }, 5000);

    return () => window.clearInterval(interval);
  }, [activeSlides.length]);

  const descriptionParagraphs = pageSettings.description
    .split(/\n\s*\n/)
    .map((paragraph) => paragraph.trim())
    .filter(Boolean);

  return (
    <MainLayout>
      <Box my={{ xs: 2, md: 4 }}>
        <Grid container spacing={{ xs: 3, md: 5 }} alignItems="stretch">
          <Grid item xs={12} lg={6}>
            <Stack spacing={3}>
              <Box display="flex" alignItems="center" gap={2}>
                <Box
                  sx={{
                    width: { xs: 72, md: 92 },
                    height: { xs: 72, md: 92 },
                    borderRadius: "24px",
                    bgcolor: "#fff5f3",
                    border: "1px solid rgba(198, 40, 40, 0.14)",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    overflow: "hidden",
                    p: 1.5,
                    flexShrink: 0,
                  }}
                >
                  <Box
                    component="img"
                    src={REDON_LOGO_CANDIDATES[logoIndex]}
                    alt="REDON logo"
                    onError={() =>
                      setLogoIndex((prev) =>
                        Math.min(prev + 1, REDON_LOGO_CANDIDATES.length - 1),
                      )
                    }
                    sx={{
                      width: "100%",
                      height: "100%",
                      objectFit: "contain",
                    }}
                  />
                </Box>
                <Box>
                  <Chip
                    label="Blood Donors Network"
                    sx={{
                      bgcolor: "#fff0ef",
                      color: "#b71c1c",
                      fontWeight: 700,
                      mb: 1,
                    }}
                  />
                  <Typography variant="h4" fontWeight={800}>
                    REDON
                  </Typography>
                </Box>
              </Box>

              <Card
                sx={{
                  borderRadius: 4,
                  p: { xs: 2.5, md: 3 },
                  background:
                    "linear-gradient(180deg, rgba(255,250,250,1) 0%, rgba(255,255,255,1) 100%)",
                  border: "1px solid rgba(183, 28, 28, 0.08)",
                  boxShadow: "0 20px 40px rgba(138, 25, 25, 0.08)",
                }}
              >
                <Stack spacing={2}>
                  {descriptionParagraphs.map((paragraph, index) => (
                    <Typography
                      key={index}
                      color="text.secondary"
                      sx={{ lineHeight: 1.85, fontSize: { xs: "0.98rem", md: "1rem" } }}
                    >
                      {paragraph}
                    </Typography>
                  ))}
                </Stack>
              </Card>

              {pageSettings.diocesanInchargeName ||
              pageSettings.diocesanInchargeContact ? (
                <Card
                  sx={{
                    borderRadius: 4,
                    p: { xs: 2.5, md: 3 },
                    bgcolor: "#fff7f7",
                    border: "1px solid rgba(183, 28, 28, 0.12)",
                  }}
                >
                  <Typography
                    variant="overline"
                    sx={{ color: "#b71c1c" }}
                    fontWeight={800}
                  >
                    Diocesan In-charge
                  </Typography>
                  {pageSettings.diocesanInchargeName ? (
                    <Typography variant="h6" fontWeight={700} mt={0.5}>
                      {pageSettings.diocesanInchargeName}
                    </Typography>
                  ) : null}
                  {pageSettings.diocesanInchargeContact ? (
                    <Typography color="text.secondary" mt={0.75}>
                      Contact: {pageSettings.diocesanInchargeContact}
                    </Typography>
                  ) : null}
                </Card>
              ) : null}
            </Stack>
          </Grid>

          <Grid item xs={12} lg={6}>
            <Card
              sx={{
                height: "100%",
                borderRadius: 4,
                p: { xs: 2, md: 3 },
                background:
                  "linear-gradient(180deg, rgba(255,245,245,1) 0%, rgba(255,255,255,1) 100%)",
                border: "1px solid rgba(183, 28, 28, 0.1)",
                boxShadow: "0 24px 48px rgba(138, 25, 25, 0.12)",
              }}
            >
              <Stack spacing={2.5}>
                <Box
                  display="flex"
                  justifyContent="space-between"
                  alignItems={{ xs: "flex-start", sm: "center" }}
                  flexDirection={{ xs: "column", sm: "row" }}
                  gap={2}
                >
                  <Box>
                    <Typography
                      variant="overline"
                      sx={{ color: "#b71c1c" }}
                      fontWeight={800}
                    >
                      Monthly Slideshow
                    </Typography>
                    <Typography variant="h5" fontWeight={800}>
                      {selectedMonth?.monthLabel || "REDON Updates"}
                    </Typography>
                  </Box>
                  <TextField
                    select
                    size="small"
                    label="Month"
                    value={selectedMonthKey}
                    onChange={(event) => setSelectedMonthKey(event.target.value)}
                    sx={{ minWidth: { xs: "100%", sm: 220 } }}
                  >
                    {monthlySlides.map((slide) => (
                      <MenuItem key={slide.docId} value={slide.monthKey}>
                        {slide.monthLabel}
                      </MenuItem>
                    ))}
                  </TextField>
                </Box>

                {activeSlideImage ? (
                  <>
                    <Box
                      sx={{
                        position: "relative",
                        borderRadius: 3,
                        overflow: "hidden",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                      }}
                    >
                      <Box
                        component="img"
                        src={activeSlideImage}
                        alt={`${selectedMonth?.monthLabel || "REDON"} slide ${currentSlideIndex + 1}`}
                        sx={{
                          width: "100%",
                          height: "auto",
                          maxHeight: { xs: 360, sm: 420, md: 520 },
                          objectFit: "contain",
                          display: "block",
                        }}
                      />
                    </Box>

                    {activeSlides.length > 1 ? (
                      <Stack direction="row" spacing={1} justifyContent="center">
                        {activeSlides.map((_, index) => (
                          <Box
                            key={index}
                            onClick={() => setCurrentSlideIndex(index)}
                            sx={{
                              width: index === currentSlideIndex ? 28 : 10,
                              height: 10,
                              borderRadius: "999px",
                              bgcolor:
                                index === currentSlideIndex
                                  ? "#b71c1c"
                                  : "rgba(183,28,28,0.22)",
                              cursor: "pointer",
                              transition: "all 0.2s ease",
                            }}
                          />
                        ))}
                      </Stack>
                    ) : null}
                  </>
                ) : (
                  <Box
                    sx={{
                      minHeight: 320,
                      borderRadius: 3,
                      border: "1px dashed rgba(183, 28, 28, 0.24)",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      textAlign: "center",
                      px: 3,
                    }}
                  >
                    <Box>
                      <Typography variant="h6" fontWeight={700}>
                        No slideshow available yet
                      </Typography>
                      <Typography color="text.secondary" mt={1}>
                        Monthly REDON images uploaded by the REDON admin will appear here.
                      </Typography>
                    </Box>
                  </Box>
                )}

                {monthlySlides.length > 1 ? (
                  <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
                    {monthlySlides.map((slide) => (
                      <Button
                        key={slide.docId}
                        size="small"
                        variant={
                          slide.monthKey === selectedMonthKey
                            ? "contained"
                            : "outlined"
                        }
                        onClick={() => setSelectedMonthKey(slide.monthKey)}
                        sx={
                          slide.monthKey === selectedMonthKey
                            ? {
                                bgcolor: "#c62828",
                                "&:hover": {
                                  bgcolor: "#b71c1c",
                                },
                              }
                            : {
                                color: "#b71c1c",
                                borderColor: "rgba(183,28,28,0.3)",
                              }
                        }
                      >
                        {slide.monthLabel}
                      </Button>
                    ))}
                  </Stack>
                ) : null}
              </Stack>
            </Card>
          </Grid>
        </Grid>
      </Box>
    </MainLayout>
  );
}
